import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Baby } from "lucide-react";
import { RELATIONSHIPS, uid } from "@/components/wizard/validation";

const emptyMember = () => ({
  id: uid(),
  name: "",
  relationship: "Spouse",
  age: "",
});

export default function FamilyStep({ family, setFamily }) {
  const add = () => setFamily((f) => [...f, emptyMember()]);
  const remove = (i) => setFamily((f) => f.filter((_, idx) => idx !== i));
  const update = (i, k, v) =>
    setFamily((f) =>
      f.map((row, idx) => (idx === i ? { ...row, [k]: v } : row))
    );

  const minorCount = family.filter((m) => Number(m.age) < 18 && m.age !== "").length;

  return (
    <div data-testid="step-family">
      <h2 className="font-serif text-2xl text-navy">Family Members</h2>
      <p className="mt-2 text-sm text-[#4A5568] sm:text-base">
        List everyone who may be a beneficiary. If any child is under 18, we'll automatically
        require a guardian later.
      </p>

      <div className="mt-6 space-y-3">
        {family.map((m, i) => {
          const age = Number(m.age);
          const minor = m.age !== "" && age < 18;
          return (
            <Card
              key={m.id}
              className={`${minor ? "border-gold/60 bg-[#FBF7EE]/60" : "border-[#0A1128]/10"}`}
              data-testid={`family-row-${i}`}
            >
              <CardContent className="grid gap-4 p-5 sm:grid-cols-[1fr_180px_110px_auto]">
                <div>
                  <Label>Name *</Label>
                  <Input
                    className="mt-2"
                    value={m.name}
                    onChange={(e) => update(i, "name", e.target.value)}
                    placeholder="Full name"
                    data-testid={`family-name-${i}`}
                  />
                </div>
                <div>
                  <Label>Relationship *</Label>
                  <Select
                    value={m.relationship}
                    onValueChange={(v) => update(i, "relationship", v)}
                  >
                    <SelectTrigger className="mt-2" data-testid={`family-rel-${i}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RELATIONSHIPS.map((r) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Age *</Label>
                  <Input
                    type="number"
                    min={0}
                    max={120}
                    className="mt-2"
                    value={m.age}
                    onChange={(e) => update(i, "age", e.target.value)}
                    placeholder="Years"
                    data-testid={`family-age-${i}`}
                  />
                </div>
                <div className="flex items-end justify-between gap-2 sm:justify-end">
                  {minor && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-gold/40 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-navy">
                      <Baby size={12} className="text-gold" />
                      Minor
                    </span>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    className="border-red-200 text-red-600 hover:bg-red-50"
                    onClick={() => remove(i)}
                    disabled={family.length === 1}
                    data-testid={`family-remove-${i}`}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Button
        type="button"
        variant="outline"
        className="mt-4 border-navy/30 text-navy hover:border-gold hover:bg-gold/10"
        onClick={add}
        data-testid="family-add-btn"
      >
        <Plus size={16} className="mr-2" /> Add another family member
      </Button>

      {minorCount > 0 && (
        <div
          className="mt-6 flex items-start gap-3 rounded-md border border-gold/40 bg-[#FBF7EE] p-4 text-sm text-navy"
          data-testid="family-minor-notice"
        >
          <span className="text-gold">⚠</span>
          <span>
            {minorCount} minor {minorCount === 1 ? "child" : "children"} detected. A
            <span className="font-medium"> Guardian step</span> will be added to this wizard.
          </span>
        </div>
      )}
    </div>
  );
}
